/* global setTimeout, clearTimeout */
/**
 * WorkdayOverlay.js
 *
 * Global host of the Munkanap surfaces, mounted once in `App.jsx` next to the WhatsNew modal so they appear over every
 * module. Exactly ONE of {status sheet, end-of-day reminder, standalone start sheet} is rendered at a time — the arbitration
 * is {@link module:helpers/workdayRules.pickSurface} (the "Mai napom" briefing outranks all of them and embeds the workday
 * card itself). Also hosts the fallback floating pill for home screens without a header slot.
 *
 * @module components/workday/WorkdayOverlay
 */

import React, {useEffect, useState} from 'react';
import {navigationRef} from '../../helpers/navigationRef';
import {useWorkday} from '../../helpers/WorkdayContext';
import WorkdayFloatingPill from './WorkdayFloatingPill';
import WorkdayReminderModal from './WorkdayReminderModal';
import WorkdaySheet from './WorkdaySheet';
import WorkdayStartSheet from './WorkdayStartSheet';

/**
 * @param {{isLoggedIn?: boolean}} props login state.
 * @returns {JSX.Element|null} overlay.
 */
export default function WorkdayOverlay({isLoggedIn}) {
    const {supported, loaded} = useWorkday();
    const [routeName, setRouteName] = useState('');

    useEffect(() => {
        if (!isLoggedIn) {
            return undefined;
        }

        const read = () => {
            try {
                setRouteName(navigationRef.isReady() ? (navigationRef.getCurrentRoute()?.name || '') : '');
            } catch (_e) {
                setRouteName('');
            }
        };

        read();

        const unsub = navigationRef.addListener ? navigationRef.addListener('state', read) : null;
        const retry = setTimeout(read, 1500);

        return () => {
            clearTimeout(retry);

            try {
                unsub?.();
            } catch (_e) {
                // already torn down
            }
        };
    }, [isLoggedIn]);

    if (!isLoggedIn || !supported || !loaded) {
        return null;
    }

    return (
        <>
            <WorkdayFloatingPill routeName={routeName} />
            <WorkdaySheet />
            <WorkdayReminderModal />
            <WorkdayStartSheet />
        </>
    );
}
